"use client";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { animated, useSpring } from "@react-spring/web";
import { ParseFloat } from "../helperFunctions";
import {
    SelectMetalType,
    SelectPurchaseType,
    selectMetalPricePerGram,
} from "../../redux/shopSlice";
import {
    selectGoldVaultBalance,
    selectSilverVaultBalance,
    selectLoading,
} from "../../redux/vaultSlice";
import ButtonLoader from "../buttonLoader";
import Image from "next/image";

// Server-rendered wrapper (conceptual, kept client-side due to "use client")
function VaultWrapper({ children }: { children: React.ReactNode }) {
    return (
        <div className="bg-themeLight001 p-0 mx-4 mt-4 justify-between rounded-lg border-1 grid grid-cols-4 sm:gap-4 items-center">
            {children}
        </div>
    );
}

// Client-side animated component
function ShowVaultBuySell() {
    const purchaseType = useSelector(SelectPurchaseType);
    const metalType = useSelector(SelectMetalType);
    const metalPricePerGram = useSelector(selectMetalPricePerGram);
    const goldVaultBalance = useSelector(selectGoldVaultBalance);
    const silverVaultBalance = useSelector(selectSilverVaultBalance);
    const loading = useSelector(selectLoading);

    const isSell = purchaseType === "sell";
    const vaultBalance = metalType === "gold" ? goldVaultBalance : silverVaultBalance;
    const metalImage = metalType === "gold" ? "/Goldbarbanner.png" : "/Silverbar.png";
    const metalAlt = metalType === "gold" ? "digital gold bar" : "digital silver bar";

    const [styles, api] = useSpring(() => ({
        opacity: isSell ? 1 : 0,
        transform: isSell ? "translateX(0%)" : "translateX(-100%)",
        config: { duration: 500 }, // Reduced from 2000ms for snappier feel
        immediate: !isSell, // Skip animation when hiding
    }), []);

    useEffect(() => {
        api.start({
            opacity: isSell ? 1 : 0,
            transform: isSell ? "translateX(0%)" : "translateX(-100%)",
        });
    }, [isSell, api]);

    if (!isSell) return null; // Early return to avoid rendering hidden content

    return (
        <animated.div style={{ ...styles, display: isSell ? "block" : "none" }}>
            <VaultWrapper>
                <div className="col-span-1 justify-center flex items-center">
                    <Image
                        src="/lottie/New Web Vault.gif"
                        alt="Vault animation"
                        width={80} // Adjusted to approximate sm:h-20
                        height={80}
                        className="h-14 sm:h-20"
                        unoptimized // GIF, no optimization available
                    />
                </div>
                <div className="col-span-3 gap-3 flex justify-around items-center">
                    <div className="flex items-center gap-2 sm:gap-4">
                        <Image
                            src={metalImage}
                            alt={metalAlt}
                            width={24} // Adjusted to approximate sm:h-6
                            height={24}
                            className="h-4 sm:h-6"
                        />
                        <p className="text-white text-sm sm:text-lg">
                            {vaultBalance != null ? vaultBalance : <ButtonLoader loading={loading} buttonText="fetching..." />} gm
                        </p>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-4">
                        <Image
                            src="/Green Rupees.png"
                            alt="rupees"
                            width={40} // Adjusted to approximate sm:w-10
                            height={40}
                            className="w-8 sm:w-10"
                        />
                        <p className="text-white text-sm sm:text-lg">
                            ₹ {ParseFloat(vaultBalance * metalPricePerGram, 2)}
                        </p>
                    </div>
                </div>
            </VaultWrapper>
        </animated.div>
    );
}

export default ShowVaultBuySell;